import { Box } from '@material-ui/core'
import { useUser } from 'components/providers/UserProvider'
import {
  editUser,
  getJobStatus,
  getUnprocessedTransactions,
  writeOnboardingTransactions,
} from 'lib/api'
import { useErrorHandler } from 'lib/hooks'
import { useOnboardingStore } from 'lib/hooks/useOnboardingStore'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import {
  backfillMissingPricingStrategyTargets,
  GAME_NAMES,
  IdGame,
  InventoryArticle,
  INVENTORY_TRANSACTION_CAUSES,
  INVENTORY_TRANSACTION_SOURCES,
  ONBOARDING_SOURCE_SYNC,
  toSlimInventoryArticleList,
} from 'shared'

export const EndOnboardingPresenter = (): JSX.Element => {
  const history = useHistory()
  const { t } = useTranslation()
  const { handle } = useErrorHandler()
  const { user, setUser } = useUser()
  const [processedGames, setProcessedGames] = useState<Record<string, string>>({})
  const [canStartPollingTransactions, setCanStartPollingTransactions] = useState(false)
  const [remainingTransactions, setRemainingTransactions] = useState<number | null>(null)
  const [finalInventoryToSave, downloadStockSource] = useOnboardingStore((state) => [
    state.finalInventoryToSave,
    state.downloadStockSource,
  ])

  /**
   *
   * Publishes the retrieved content to the PowerTools database, after the user has chosen which prices and comments to apply for each item.
   * Handles content differently based on the data source
   */
  const handlePublish = async (idGame: IdGame, inventory: InventoryArticle[]) => {
    try {
      if (downloadStockSource === ONBOARDING_SOURCE_SYNC.CARDMARKET) {
        await writeOnboardingTransactions(
          user,
          idGame,
          toSlimInventoryArticleList(inventory),
          true,
          INVENTORY_TRANSACTION_CAUSES.ONBOARDING_SYNC,
          INVENTORY_TRANSACTION_SOURCES.CARDMARKET
        )
      } else if (downloadStockSource === ONBOARDING_SOURCE_SYNC.CSV) {
        await writeOnboardingTransactions(
          user,
          idGame,
          toSlimInventoryArticleList(inventory),
          true,
          INVENTORY_TRANSACTION_CAUSES.ONBOARDING_CSV
        )
      }
      // else if (downloadStockSource === ONBOARDING_SOURCE_SYNC.CARDTRADER) {
      //   const convertedData = convertInventoryArticleToCardTraderStockToSave(inventory)
      //   await saveUpdatedCardTraderStock(convertedData)
      // }
    } catch (err) {
      handle(err)
    }
  }

  /**
   * Completes onboarding: backfills the missing pricing-strategy targets and persists
   * both pricingStrategies and hasOnboarded in a single atomic update, then routes home.
   */
  const finishOnboarding = async () => {
    backfillMissingPricingStrategyTargets(user.pricingStrategies)

    const update = await editUser({
      pricingStrategies: user.pricingStrategies,
      hasOnboarded: true,
    })
    setUser({ ...user, ...update })
    history.push('/')
  }

  useEffect(() => {
    if (downloadStockSource === ONBOARDING_SOURCE_SYNC.NO_STOCK) {
      // skip import: nothing to publish or wait for, complete onboarding directly
      finishOnboarding().catch(handle)
      return
    }

    const gameIds = Object.keys(finalInventoryToSave)

    const initialState: Record<string, string> = {}
    gameIds.forEach((id) => {
      initialState[GAME_NAMES[Number(id)]] = ''
    })

    setProcessedGames(initialState)

    const startJob = async () => {
      const processNextGame = async (index: number) => {
        if (index >= gameIds.length) {
          // await fetchUser()
          setCanStartPollingTransactions(true) // next polling
          return
        }

        const idGame: any = gameIds[index]

        const jobId = 'writeOnboardingTransactions-' + user._id + '-' + idGame
        const existingStatus = await getJobStatus(jobId)

        if (!existingStatus) {
          await handlePublish(Number(idGame), finalInventoryToSave[idGame])
        }

        const interval = setInterval(async () => {
          const status = await getJobStatus(jobId)

          if (status === 'completed') {
            clearInterval(interval)
            setProcessedGames((prev) => ({
              ...prev,
              [GAME_NAMES[idGame]]: status,
            }))
            processNextGame(index + 1) // go to next game
          } else if (status === 'failed') {
            setProcessedGames((prev) => ({
              ...prev,
              [GAME_NAMES[idGame]]: status,
            }))
            clearInterval(interval)
            processNextGame(index + 1) // go to next game
          }
        }, 3000)
      }

      processNextGame(0) // start from first game
    }

    startJob().catch(handle)
  }, [])

  useEffect(() => {
    if (!canStartPollingTransactions) return

    const interval = setInterval(async () => {
      try {
        const res = await getUnprocessedTransactions()
        const powertoolsEntry = res.data.data.find((t: any) => t.type === 'PowerTools')

        if (!powertoolsEntry || powertoolsEntry.quantity_unprocessed === 0) {
          clearInterval(interval)
          await finishOnboarding()
        } else {
          setRemainingTransactions(powertoolsEntry.quantity_unprocessed)
        }
      } catch (err) {
        console.error('Error in writing transactions', err)
      }
    }, 4000)

    return () => clearInterval(interval)
  }, [canStartPollingTransactions, history])

  return (
    <>
      <div className="d-flex flex-column justify-content-center align-items-center px-5 pb-5 pt-2 pt-xl-5">
        <div className="onboarding-container">
          <div className="container-small">
            <div className="w-100 text-center mb-4">
              <i className="fa-solid fa-gear fa-spin spinner-gradient"></i>
            </div>
            {downloadStockSource !== ONBOARDING_SOURCE_SYNC.NO_STOCK && (
              <>
                <h1 className="congratulations-onboarding text-center">
                  {t('onboarding.congratulations')}!
                </h1>
                <h4 className="fw-light mb-4 mb-xl-5 text-center">
                  {t('onboarding.endOnboarding1')}
                </h4>
              </>
            )}

            {downloadStockSource !== 'noStock' && (
              <div className="onboarding-notes-box mb-5">
                <h5>{t('onboarding.importStatus')}</h5>
                <div className="d-flex flex-wrap align-items-center justify-content-center w-100">
                  {Object.entries(processedGames).map(([game, status]) => (
                    <Box
                      width={80}
                      key={game}
                      className="d-flex flex-column justify-content-center align-items-center my-2"
                    >
                      <span className="m-1 fs-6">{game}</span>
                      <Box height={30}>
                        {status === 'completed' && (
                          <i
                            className="fa-regular fa-circle-check success-color fs-6"
                            style={{ fontSize: '20px ' }}
                          ></i>
                        )}
                        {status === 'failed' && (
                          <i
                            data-testid={`failed-game-${game}`}
                            className="fa-regular fa-triangle-exclamation error-color fs-6"
                          ></i>
                        )}
                        {status === 'active' && (
                          <i className="fa-regular fa-spinner fa-spin fs-6"></i>
                        )}
                      </Box>
                    </Box>
                  ))}
                </div>
              </div>
            )}

            <p>{t('onboarding.doNotRefreshPage')}</p>
            <p className="mt-3">
              {remainingTransactions === null ? (
                ''
              ) : (
                <span>
                  {t('spinner.remaining')} {t('spinner.changes')}: {remainingTransactions}{' '}
                </span>
              )}
            </p>
            <div className="onboarding-notes-box mb-5">
              <h4 className="fw-light my-2">{t('onboarding.endOnboarding2')}</h4>
              <h5 className="fw-light my-4">{t('onboarding.endOnboarding3')}</h5>
              <h5 className="fw-light my-4">{t('onboarding.endOnboarding4')}</h5>
              <h5 className="fw-light my-2">{t('onboarding.endOnboarding5')}</h5>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
